import { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  computeRunningBalances,
  deriveStockCardReference,
  qInventoryItemsList,
  qStockCardMovements,
  type InventoryItemRow,
  type StockCardMovementRow,
  type StockCardMovementVM,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { type ShellOutletContext } from '../../shell/shared';
import {
  FilterDropdown,
  InventoryListToolbar,
  InventoryPageShell,
  InventoryRow,
  InventoryTable,
} from './_shared';

const ALL = 'all';

const RANGE_OPTIONS = [
  { value: ALL, label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
] as const;

const MOVEMENT_TYPE_OPTIONS = [
  { value: ALL, label: 'All movements' },
  { value: 'adjustment_add', label: 'Adjustment — Add' },
  { value: 'adjustment_deduct', label: 'Adjustment — Deduct' },
  { value: 'delivery_received', label: 'Delivery received' },
  { value: 'pharmacy_dispense', label: 'Pharmacy dispense' },
  // PRD.208 — surface Product Release alongside Pharmacy Dispense.
  { value: 'product_release', label: 'Product release' },
] as const;

const SOURCE_TYPE_OPTIONS = [
  { value: ALL, label: 'All sources' },
  { value: 'inventory_adjustment', label: 'Inventory Adjustment' },
  { value: 'delivery_order', label: 'Delivery Order' },
  { value: 'pharmacy_dispense', label: 'Pharmacy Dispense' },
  // PRD.208 — Product Release source filter.
  { value: 'product_release', label: 'Product Release' },
] as const;

const COLUMNS = [
  { label: 'Date', width: '110px' },
  { label: 'Item', width: '1.4fr' },
  { label: 'Batch', width: '160px' },
  { label: 'Source', width: '160px' },
  { label: 'Reference', width: '150px' },
  { label: 'Type', width: '140px' },
  { label: 'In', width: '90px', align: 'right' as const },
  { label: 'Out', width: '90px', align: 'right' as const },
  { label: 'Balance', width: '110px', align: 'right' as const },
  { label: 'Remarks', width: '1fr' },
];

function formatNum(value: number | null | undefined, fallback = '—'): string {
  if (value == null) return fallback;
  if (Number.isInteger(value)) return value.toString();
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

function rangeStartDate(value: string): string | null {
  if (value === ALL) return null;
  const days = value === '7d' ? 7 : value === '30d' ? 30 : value === '90d' ? 90 : 0;
  if (!days) return null;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function movementTypeLabel(value: string | null): string {
  switch (value) {
    case 'adjustment_add':
      return 'Adj — Add';
    case 'adjustment_deduct':
      return 'Adj — Deduct';
    case 'delivery_received':
      return 'Delivery';
    case 'pharmacy_dispense':
      return 'Dispense';
    case 'product_release':
      return 'Product Release';
    case 'opening_balance':
      return 'Opening';
    case 'correction':
      return 'Correction';
    default:
      return value ?? '—';
  }
}

export function StockCardPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();

  // Load every movement once. The join + LEFT JOINs mean each row already
  // carries item / batch / source metadata. Filtering happens in JS so the
  // QuerySpec stays cache-stable while the user toggles filters.
  const { data: movementRows = [] } =
    usePowerSyncQuery<StockCardMovementRow>(qStockCardMovements());
  const { data: itemRows = [] } = usePowerSyncQuery<InventoryItemRow>(qInventoryItemsList());

  // Compute per-item running balances against the FULL ledger so that a
  // filtered slice of a single item still shows correct cumulative
  // balances at each surfaced movement.
  const enriched: StockCardMovementVM[] = useMemo(
    () => computeRunningBalances(movementRows, 'desc'),
    [movementRows],
  );

  const [search, setSearch] = useState('');
  const [itemFilter, setItemFilter] = useState<string>(ALL);
  const [batchFilter, setBatchFilter] = useState<string>(ALL);
  const [rangeFilter, setRangeFilter] = useState<(typeof RANGE_OPTIONS)[number]['value']>(ALL);
  const [movementTypeFilter, setMovementTypeFilter] =
    useState<(typeof MOVEMENT_TYPE_OPTIONS)[number]['value']>(ALL);
  const [sourceTypeFilter, setSourceTypeFilter] =
    useState<(typeof SOURCE_TYPE_OPTIONS)[number]['value']>(ALL);

  // Build item filter options from rows that actually have movements +
  // catalog items, so newly-added inventory shows up immediately.
  const itemOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of enriched) {
      if (row.inventory_item_id && row.item_name) {
        seen.set(row.inventory_item_id, row.item_name);
      }
    }
    for (const item of itemRows) {
      if (!seen.has(item.id) && item.item_name) {
        seen.set(item.id, item.item_name);
      }
    }
    const opts = Array.from(seen.entries()).map(([id, name]) => ({ value: id, label: name }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: ALL, label: 'All items' }, ...opts];
  }, [enriched, itemRows]);

  // Batch filter is item-scoped — populated from movements of the
  // currently-filtered item only.
  const batchOptions = useMemo(() => {
    if (itemFilter === ALL) {
      return [{ value: ALL, label: 'All batches' }];
    }
    const seen = new Map<string, { label: string; expiry: string | null }>();
    for (const row of enriched) {
      if (row.inventory_item_id !== itemFilter) continue;
      const id = row.inventory_batch_id ?? '__none__';
      if (seen.has(id)) continue;
      seen.set(id, {
        label: row.batch_number ?? (row.inventory_batch_id ? id.slice(0, 8) : 'No batch'),
        expiry: row.expiry_date ?? null,
      });
    }
    const opts = Array.from(seen.entries()).map(([value, { label, expiry }]) => ({
      value,
      label: expiry ? `${label} · exp ${expiry}` : label,
    }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: ALL, label: 'All batches' }, ...opts];
  }, [enriched, itemFilter]);

  // Reset batch filter when item changes — keeps the dropdown valid.
  const effectiveBatchFilter = useMemo(() => {
    if (itemFilter === ALL) return ALL;
    if (batchFilter === ALL) return ALL;
    const exists = batchOptions.some((o) => o.value === batchFilter);
    return exists ? batchFilter : ALL;
  }, [batchFilter, batchOptions, itemFilter]);

  // Apply filters in display order. Running balance was already computed
  // before filtering, so each surfaced row's `running_balance` reflects
  // its true position in the unfiltered ledger.
  const filtered = useMemo(() => {
    const fromDate = rangeStartDate(rangeFilter);
    const q = search.trim().toLowerCase();
    return enriched.filter((row) => {
      if (itemFilter !== ALL && row.inventory_item_id !== itemFilter) return false;
      if (effectiveBatchFilter !== ALL) {
        const id = row.inventory_batch_id ?? '__none__';
        if (id !== effectiveBatchFilter) return false;
      }
      if (movementTypeFilter !== ALL && row.movement_type !== movementTypeFilter) return false;
      if (sourceTypeFilter !== ALL && row.source_type !== sourceTypeFilter) return false;
      if (fromDate && row.movement_date && row.movement_date < fromDate) return false;
      if (q) {
        // PRD.208 — search now also matches the joined human-readable
        // reference fields so users can find a movement by patient or
        // invoice without knowing the dispense/release/DO number.
        const haystack = [
          row.item_name,
          row.sku,
          row.batch_number,
          row.reference_no,
          row.remarks,
          row.source_id,
          row.patient_name,
          row.invoice_no,
          row.dispense_number,
          row.release_number,
          row.do_number,
          row.do_supplier_name,
          row.supplier_invoice_number,
          row.adjustment_number,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [
    enriched,
    search,
    itemFilter,
    effectiveBatchFilter,
    rangeFilter,
    movementTypeFilter,
    sourceTypeFilter,
  ]);

  // Summary cards reflect the full ledger for the selected item (not the
  // filtered slice) so they remain meaningful when search/date filters
  // would zero out the visible rows.
  const summary = useMemo(() => {
    if (itemFilter === ALL) return null;
    let totalIn = 0;
    let totalOut = 0;
    let current = 0;
    for (const row of enriched) {
      if (row.inventory_item_id !== itemFilter) continue;
      totalIn += row.quantity_in ?? 0;
      totalOut += row.quantity_out ?? 0;
    }
    current = totalIn - totalOut;
    return { totalIn, totalOut, current };
  }, [enriched, itemFilter]);

  return (
    <InventoryPageShell
      title="Stock Card"
      subtitle="Read-only stock movement ledger for inventory items and batches."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
    >
      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search item, SKU, batch, reference, remarks..."
        filters={
          <>
            <FilterDropdown
              label="Item"
              value={itemFilter}
              options={itemOptions}
              onChange={setItemFilter}
            />
            <FilterDropdown
              label="Batch"
              value={effectiveBatchFilter}
              options={batchOptions}
              onChange={setBatchFilter}
            />
            <FilterDropdown
              label="Range"
              value={rangeFilter}
              options={RANGE_OPTIONS}
              onChange={setRangeFilter}
            />
            <FilterDropdown
              label="Type"
              value={movementTypeFilter}
              options={MOVEMENT_TYPE_OPTIONS}
              onChange={setMovementTypeFilter}
            />
            <FilterDropdown
              label="Source"
              value={sourceTypeFilter}
              options={SOURCE_TYPE_OPTIONS}
              onChange={setSourceTypeFilter}
            />
          </>
        }
      />

      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />

      {summary && (
        <div
          style={{
            padding: '0 var(--sp-6)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'var(--sp-3)',
            flexShrink: 0,
            marginBottom: 'var(--sp-4)',
          }}
        >
          <SummaryCard label="Current Stock" value={formatNum(summary.current)} accent="primary" />
          <SummaryCard label="Total In" value={formatNum(summary.totalIn)} accent="positive" />
          <SummaryCard label="Total Out" value={formatNum(summary.totalOut)} accent="negative" />
        </div>
      )}

      {/* Read-only helper line — direct users to Inventory Adjustments */}
      <div
        style={{
          padding: '0 var(--sp-6) var(--sp-3)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          flexShrink: 0,
        }}
      >
        Stock movements come from Inventory Adjustments and Delivery Orders. To correct stock,
        create an Inventory Adjustment.
      </div>

      <InventoryTable
        columns={COLUMNS}
        empty={
          filtered.length === 0
            ? {
                show: true,
                title: 'No stock movements found.',
                hint:
                  enriched.length === 0
                    ? 'Create an Inventory Adjustment or receive a Delivery Order to generate stock movements.'
                    : 'Try a different filter or search.',
              }
            : undefined
        }
      >
        {filtered.map((row) => (
          <InventoryRow key={row.movement_id} columns={COLUMNS}>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {row.movement_date ?? '—'}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.item_name ?? '(item not synced)'}
              </div>
              {row.sku && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  SKU: {row.sku}
                </div>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                {row.batch_number ??
                  (row.inventory_batch_id ? row.inventory_batch_id.slice(0, 8) : '—')}
              </div>
              {row.expiry_date && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  Exp {row.expiry_date}
                </div>
              )}
            </div>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {row.source_label}
            </span>
            <ReferenceCell row={row} onNavigate={(href) => navigate(href)} />
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {movementTypeLabel(row.movement_type)}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color:
                  row.quantity_in != null && row.quantity_in > 0
                    ? 'var(--color-success-text)'
                    : 'var(--text-muted)',
                textAlign: 'right',
              }}
            >
              {row.quantity_in != null && row.quantity_in > 0
                ? `+${formatNum(row.quantity_in)}`
                : '—'}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color:
                  row.quantity_out != null && row.quantity_out > 0
                    ? '#991b1b'
                    : 'var(--text-muted)',
                textAlign: 'right',
              }}
            >
              {row.quantity_out != null && row.quantity_out > 0
                ? `-${formatNum(row.quantity_out)}`
                : '—'}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
                textAlign: 'right',
              }}
            >
              {formatNum(row.running_balance)}
              {row.unit && (
                <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>
                  {' '}
                  {row.unit}
                </span>
              )}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={row.remarks ?? undefined}
            >
              {row.remarks ?? '—'}
            </span>
          </InventoryRow>
        ))}
      </InventoryTable>
    </InventoryPageShell>
  );
}

// ─── PRD.208 — Source-aware Reference cell ───

function ReferenceCell({
  row,
  onNavigate,
}: {
  row: StockCardMovementVM;
  onNavigate: (href: string) => void;
}) {
  const ref = deriveStockCardReference(row);
  const linkColor = 'var(--color-primary)';
  return (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {ref.href ? (
        <button
          type="button"
          onClick={() => onNavigate(ref.href!)}
          title={`Open ${ref.primary_label}`}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            textAlign: 'left',
            color: linkColor,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
          }}
        >
          {ref.primary_label}
        </button>
      ) : (
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {ref.primary_label}
        </span>
      )}
      {ref.secondary_label && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-family-mono, monospace)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {ref.secondary_label}
        </span>
      )}
    </div>
  );
}

// ─── Summary card ───

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'primary' | 'positive' | 'negative';
}) {
  const accentColor =
    accent === 'positive'
      ? 'var(--color-success-text)'
      : accent === 'negative'
        ? '#991b1b'
        : 'var(--text)';
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-1)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 'var(--font-weight-semi)',
          color: accentColor,
        }}
      >
        {value}
      </span>
    </div>
  );
}
